// Agreed dates entry, for people on an "Agreed dates" contract — the only days such a worker
// carries expected hours on.
//
// Patterns vary from fully regular to fully random (two weeks on / two off, or 2 days one week and
// 0 the next), so there is no single right way in. Four ways all write the SAME editable list of
// dates, and whatever a helper fills you can still adjust by clicking:
//   • Period      — set a window, then pick specific days inside it (the grid dims the rest)
//   • Block fill  — agree every working day in that window at once
//   • Copy        — last month's pattern shifted four weeks, so weekdays stay aligned
//   • Cycle       — a rotation like "2 weeks on, 2 off" filled across the month
import React, { useCallback, useEffect, useState } from 'react';
import { authFetch, API } from './api';

interface Person {
  username: string; display_name: string;
  weekly_days: number[]; fulfilment: string | null;
}

const inp = 'bg-gray-800 border border-gray-700 text-gray-200 text-xs rounded px-2 py-1 focus:outline-none focus:border-blue-500';
const btn = 'px-2.5 py-1 text-xs rounded-lg';
const DOW = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

const pad = (n: number) => String(n).padStart(2, '0');
const isoOf = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`;
// 0 = Monday
const dowOf = (y: number, m: number, d: number) => (new Date(y, m - 1, d).getDay() + 6) % 7;
const mondayOf = (dt: Date) => {
  const c = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
  c.setDate(c.getDate() - ((c.getDay() + 6) % 7));
  return c;
};
const daysBetween = (a: Date, b: Date) => Math.round((b.getTime() - a.getTime()) / 86400000);

export default function PlannedDatesPanel({ year, month }: { year: number; month: number }) {
  const [open, setOpen] = useState(false);
  const [people, setPeople] = useState<Person[]>([]);
  const [who, setWho] = useState('');
  const [dates, setDates] = useState<Set<string>>(new Set());
  const [saved, setSaved] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<{ added: string[]; removed: string[]; skipped: { date: string; reason: string }[] } | null>(null);

  // Period: scopes what you can click, and powers the block fill.
  const [pFrom, setPFrom] = useState('');
  const [pTo, setPTo] = useState('');
  // Cycle: weeks on / weeks off, anchored to a week you choose.
  const [cOn, setCOn] = useState('2');
  const [cOff, setCOff] = useState('2');
  const [cFrom, setCFrom] = useState('');

  const lastDay = new Date(year, month, 0).getDate();
  const firstDow = dowOf(year, month, 1);
  const person = people.find(p => p.username === who);
  const weekly = person?.weekly_days || [1, 2, 3, 4, 5];
  const isContracted = (d: number) => weekly.includes(dowOf(year, month, d) + 1);

  const loadPeople = useCallback(async () => {
    const r = await authFetch(`${API}/statistics/employment`);
    if (!r.ok) { setErr('Could not load people'); return; }
    const d = await r.json();
    const list: Person[] = (d.people || [])
      .filter((p: any) => p.current?.contract_type === 'planned_dates')
      .map((p: any) => ({
        username: p.username, display_name: p.display_name,
        weekly_days: p.current.weekly_days || [1, 2, 3, 4, 5],
        fulfilment: p.current.fulfilment || null,
      }));
    setPeople(list);
    setWho(prev => prev || (list[0]?.username ?? ''));
  }, []);

  const loadDates = useCallback(async (u: string) => {
    if (!u) return;
    setErr(null); setResult(null);
    const r = await authFetch(`${API}/statistics/calendar/planned-dates?username=${encodeURIComponent(u)}&year=${year}&month=${month}`);
    if (!r.ok) { setErr('Could not load agreed dates'); return; }
    const d = await r.json();
    setDates(new Set(d.dates || []));
    setSaved(new Set(d.dates || []));
  }, [year, month]);

  useEffect(() => { if (open) loadPeople(); }, [open, loadPeople]);
  useEffect(() => { if (open && who) loadDates(who); }, [open, who, loadDates]);

  const inPeriod = (d: number) => {
    if (!pFrom || !pTo) return true;
    const k = isoOf(year, month, d);
    return k >= pFrom && k <= pTo;
  };
  const periodActive = !!(pFrom && pTo);

  const toggle = (d: number) => {
    if (periodActive && !inPeriod(d)) return;
    setDates(prev => {
      const next = new Set(prev); const k = isoOf(year, month, d);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  // Block fill: every contracted weekday inside the period.
  const fillPeriod = () => {
    if (!periodActive) { setErr('Set a period first'); return; }
    setErr(null);
    setDates(prev => {
      const next = new Set(prev);
      for (let d = 1; d <= lastDay; d++) {
        if (inPeriod(d) && isContracted(d)) next.add(isoOf(year, month, d));
      }
      return next;
    });
  };
  const clearPeriodDays = () => {
    if (!periodActive) return;
    setDates(prev => {
      const next = new Set(prev);
      for (let d = 1; d <= lastDay; d++) if (inPeriod(d)) next.delete(isoOf(year, month, d));
      return next;
    });
  };

  // Copy: last month's dates + 28 days. Four weeks keeps the weekday alignment exact, which is
  // what matters for any on/off rotation.
  const copyPrevious = async () => {
    setErr(null); setResult(null);
    const py = month === 1 ? year - 1 : year;
    const pm = month === 1 ? 12 : month - 1;
    const r = await authFetch(`${API}/statistics/calendar/planned-dates?username=${encodeURIComponent(who)}&year=${py}&month=${pm}`);
    if (!r.ok) { setErr('Could not read last month'); return; }
    const d = await r.json();
    const moved: string[] = [];
    (d.dates || []).forEach((s: string) => {
      const [yy, mm, dd] = s.split('-').map(Number);
      const t = new Date(yy, mm - 1, dd);
      t.setDate(t.getDate() + 28);
      if (t.getFullYear() === year && t.getMonth() + 1 === month) {
        moved.push(isoOf(year, month, t.getDate()));
      }
    });
    if (moved.length === 0) { setErr('Nothing from last month lands in this month'); return; }
    setDates(new Set(moved));
  };

  // Cycle: N weeks on, N off, counted from the anchor week.
  const fillCycle = () => {
    const on = parseInt(cOn, 10), off = parseInt(cOff, 10);
    if (!(on > 0) || !(off >= 0)) { setErr('Weeks on must be at least 1'); return; }
    if (!cFrom) { setErr('Pick the first day of a working week'); return; }
    setErr(null);
    const [ay, am, ad] = cFrom.split('-').map(Number);
    const anchorMon = mondayOf(new Date(ay, am - 1, ad));
    const cycle = on + off;
    setDates(prev => {
      const next = new Set(prev);
      for (let d = 1; d <= lastDay; d++) {
        if (!isContracted(d)) continue;
        const mon = mondayOf(new Date(year, month - 1, d));
        const weeks = Math.round(daysBetween(anchorMon, mon) / 7);
        const pos = ((weeks % cycle) + cycle) % cycle;   // safe for negative weeks
        if (pos < on) next.add(isoOf(year, month, d));
      }
      return next;
    });
  };

  const dirty = dates.size !== saved.size || Array.from(dates).some(d => !saved.has(d));

  const save = async () => {
    setBusy(true); setErr(null); setResult(null);
    const r = await authFetch(`${API}/statistics/calendar/planned-dates`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: who, year, month, dates: Array.from(dates).sort() }),
    });
    setBusy(false);
    if (!r.ok) {
      let m = `${r.status}`;
      try { m = (await r.json()).detail || m; } catch { /* keep status */ }
      setErr(m); return;
    }
    setResult(await r.json());
    loadDates(who);
  };

  const monthName = new Date(year, month - 1, 1).toLocaleString('en', { month: 'long' });

  return (
    <div className="mt-3">
      <button onClick={() => { setOpen(o => !o); setErr(null); setResult(null); }}
        className="text-xs text-gray-400 hover:text-white bg-gray-800 hover:bg-gray-700 px-3 py-1.5 rounded-lg">
        {open ? '▾' : '▸'} Agreed dates (for “Agreed dates” contracts)
      </button>

      {open && (
        <div className="mt-2 bg-[#1a1d27] border border-gray-800 rounded-xl p-4">
          {people.length === 0 ? (
            <p className="text-xs text-gray-500">
              Nobody is on an “Agreed dates” contract. Set one under Work time → Workforce → Terms,
              then their agreed days can be picked here.
            </p>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <label className="text-xs text-gray-500">Person
                  <select className={`${inp} ml-2`} value={who} onChange={e => setWho(e.target.value)}>
                    {people.map(p => <option key={p.username} value={p.username}>{p.display_name}</option>)}
                  </select>
                </label>
                <span className="text-xs text-gray-600">{monthName} {year}</span>
                <span className="ml-auto text-xs text-gray-400">
                  {dates.size} day{dates.size === 1 ? '' : 's'} agreed
                </span>
              </div>
              <p className="text-[11px] text-gray-500 mb-3">
                These are the only days this person is expected to work — every other day carries no
                expectation and nothing owed.
                {person?.fulfilment === 'full_day' && (
                  <span className="text-gray-400"> An agreed day counts only if it is worked in
                  full, so a half-day shows as missed AND as hours owed.</span>
                )}
              </p>

              {/* Period — scopes the grid and drives the block fill */}
              <div className="flex flex-wrap items-end gap-2 mb-2 pb-3 border-b border-gray-800">
                <label className="text-xs text-gray-500">Period
                  <input type="date" className={`${inp} ml-2`} value={pFrom}
                    onChange={e => setPFrom(e.target.value)} />
                </label>
                <label className="text-xs text-gray-500">to
                  <input type="date" className={`${inp} ml-2`} value={pTo}
                    onChange={e => setPTo(e.target.value)} />
                </label>
                <button onClick={fillPeriod} disabled={!periodActive}
                  className={`${btn} bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white`}>
                  Agree all working days
                </button>
                <button onClick={clearPeriodDays} disabled={!periodActive}
                  className={`${btn} bg-gray-800 hover:bg-gray-700 disabled:opacity-40 text-gray-300`}>
                  Clear in period
                </button>
                {periodActive && (
                  <button onClick={() => { setPFrom(''); setPTo(''); }}
                    className={`${btn} text-gray-500 hover:text-gray-300`}>
                    Unlock whole month
                  </button>
                )}
                {periodActive && (
                  <span className="text-[11px] text-amber-500/80">
                    Only days inside the period can be clicked
                  </span>
                )}
              </div>

              {/* Repeating helpers */}
              <div className="flex flex-wrap items-end gap-2 mb-3 pb-3 border-b border-gray-800">
                <button onClick={copyPrevious}
                  className={`${btn} bg-gray-800 hover:bg-gray-700 text-gray-300`}
                  title="Last month's pattern shifted 4 weeks, so weekdays line up">
                  Copy last month
                </button>
                <span className="text-xs text-gray-600 ml-2">Cycle:</span>
                <input type="number" min={1} max={8} className={`${inp} w-12`} value={cOn}
                  onChange={e => setCOn(e.target.value)} />
                <span className="text-xs text-gray-500">wks on</span>
                <input type="number" min={0} max={8} className={`${inp} w-12`} value={cOff}
                  onChange={e => setCOff(e.target.value)} />
                <span className="text-xs text-gray-500">off, first working week starts</span>
                <input type="date" className={inp} value={cFrom}
                  onChange={e => setCFrom(e.target.value)} />
                <button onClick={fillCycle}
                  className={`${btn} bg-gray-800 hover:bg-gray-700 text-gray-300`}>
                  Fill month
                </button>
              </div>

              {/* Grid */}
              <div className="grid grid-cols-7 gap-1 max-w-md">
                {DOW.map(d => (
                  <div key={d} className="text-[10px] text-gray-600 text-center pb-0.5">{d}</div>
                ))}
                {Array.from({ length: firstDow }).map((_, i) => <div key={`pad${i}`} />)}
                {Array.from({ length: lastDay }, (_, i) => i + 1).map(d => {
                  const on = dates.has(isoOf(year, month, d));
                  const contracted = isContracted(d);
                  const locked = periodActive && !inPeriod(d);
                  return (
                    <button key={d} onClick={() => toggle(d)} disabled={locked}
                      title={locked ? 'Outside the selected period' : undefined}
                      className={`h-8 text-xs rounded transition-colors ${
                        locked ? 'bg-gray-900/40 text-gray-700 cursor-not-allowed'
                          : on ? 'bg-blue-600 text-white font-medium'
                            : contracted ? 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                              : 'bg-gray-900 text-gray-600 hover:bg-gray-800'}`}>
                      {d}
                    </button>
                  );
                })}
              </div>

              {err && <p className="text-xs text-red-400 mt-3">{err}</p>}
              {result && (
                <div className="mt-3 text-xs">
                  {result.added.length > 0 && <span className="text-green-400">+{result.added.length} agreed. </span>}
                  {result.removed.length > 0 && <span className="text-amber-400">−{result.removed.length} removed. </span>}
                  {result.added.length === 0 && result.removed.length === 0 && <span className="text-gray-500">No change. </span>}
                  {result.skipped.length > 0 && (
                    <span className="text-gray-500">
                      Left alone: {result.skipped.map(x => `${x.date.slice(5)} (${x.reason})`).join(', ')}
                    </span>
                  )}
                </div>
              )}

              <div className="flex items-center gap-2 mt-3">
                <button onClick={save} disabled={busy || !dirty}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-xs rounded-lg font-medium">
                  {busy ? 'Saving…' : dirty ? 'Save agreed dates' : 'Saved'}
                </button>
                {dirty && (
                  <button onClick={() => setDates(new Set(saved))}
                    className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-xs rounded-lg">
                    Undo changes
                  </button>
                )}
                <span className="text-[11px] text-gray-600 ml-1">
                  A block crossing month-end is entered in each month separately.
                </span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
